import { Link, useRouter } from 'expo-router';
import Globe from 'lucide-react-native/icons/globe';
import Heart from 'lucide-react-native/icons/heart';
import Menu from 'lucide-react-native/icons/menu';
import X from 'lucide-react-native/icons/x';
import { useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { RivieraLogo } from '@/components/riviera-logo';
import { navHref, navLinks, type NavPathname } from '@/components/riviera/nav-links';
import { Button } from '@/components/ui/button';
import { Dropdown } from '@/components/ui/dropdown';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { LOCALE_NAMES, SUPPORTED_LOCALES, type Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';

import css from './site-header.module.css';

const languageOptions = SUPPORTED_LOCALES.map((l) => ({ value: l, label: LOCALE_NAMES[l] }));

// Web-only: desktop nav vs hamburger, the sm-gated actions, and the row's
// padding are all driven by site-header.module.css media queries so they
// resolve at first paint. The native site-header.tsx keeps the JS isSm/isLg
// version (no SSR/hydration timing issue there). The mobile menu Modal is
// unchanged — it only opens on a tap, so it's not a layout-shift source.
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();

  // Used only by the mobile menu, whose items live inside a <Modal> that isn't
  // mounted until it opens — nothing there is ever in the crawlable DOM, so it
  // stays imperative and can close the sheet before navigating.
  const goTo = (pathname: NavPathname) => {
    router.push(navHref(pathname, locale));
  };

  return (
    <View
      style={[
        styles.header,
        { backgroundColor: theme.card, borderBottomColor: theme.border },
      ]}>
      <div className={css.row}>
        {/* asChild + Pressable renders a real <a href> — react-native-web maps
            the href Link passes down onto an anchor element. Layout lives on
            the inner View, not Pressable's own style: expo-router's asChild
            merges props via a Radix Slot that silently drops Pressable's
            function-style (state => [...]) prop, spreading it as an empty
            object — see node_modules/expo-router/build/ui/Slot.js. */}
        <Link href={navHref('/[lang]', locale)} asChild>
          <Pressable
            accessibilityRole="link"
            accessibilityLabel={t('a11y.home')}
            style={({ pressed }) => pressed && styles.pressed}>
            <View style={styles.brandStack}>
              <RivieraLogo size={36} color={theme.gold} />
              <View>
                <Text
                  style={[
                    styles.brandTitle,
                    { color: theme.primary, fontFamily: Fonts?.serif },
                  ]}>
                  RIVIERA
                </Text>
                <View style={styles.brandSubRow}>
                  <View style={[styles.brandSubRule, { backgroundColor: theme.gold }]} />
                  <Text style={[styles.brandSub, { color: theme.navy }]}>TO DO</Text>
                  <View style={[styles.brandSubRule, { backgroundColor: theme.gold }]} />
                </View>
              </View>
            </View>
          </Pressable>
        </Link>

        <div className={css.desktopNav}>
          {navLinks.map((link) => (
            <Link key={link.key} href={navHref(link.pathname, locale)} asChild>
              <Pressable
                style={({ pressed }) => [styles.desktopNavLink, pressed && styles.pressed]}>
                <Text style={{ color: theme.text, fontSize: 14, fontWeight: '500' }}>
                  {t(`nav.${link.key}`)}
                </Text>
              </Pressable>
            </Link>
          ))}
        </div>

        <div className={css.actions}>
          <Dropdown
            options={languageOptions}
            value={locale}
            onChange={(v) => setLocale(v as Locale)}
            icon={Globe}
            label={locale.toUpperCase()}
            style={styles.iconBtn}
            labelStyle={{ color: theme.text, fontSize: 14, fontWeight: '500' }}
          />
          <div className={css.smUp}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('a11y.favorites')}
              style={({ pressed }) => [styles.iconSquare, pressed && styles.pressed]}>
              <Heart size={18} color={theme.text} />
            </Pressable>
          </div>
          <Button variant="gold" size="sm">
            {t('app.logIn')}
          </Button>
          <div className={css.hamburger}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('a11y.menu')}
              onPress={() => setOpen(true)}
              style={({ pressed }) => [styles.iconSquare, pressed && styles.pressed]}>
              <Menu size={20} color={theme.text} />
            </Pressable>
          </div>
        </div>
      </div>

      <Modal
        visible={open}
        animationType="slide"
        transparent
        onRequestClose={() => setOpen(false)}>
        <View style={styles.modalBackdrop}>
          <SafeAreaView
            edges={['top']}
            style={[styles.modalSheet, { backgroundColor: theme.card }]}>
            <View style={styles.modalHeader}>
              <View style={styles.brandRow}>
                <RivieraLogo size={32} color={theme.gold} />
                <Text
                  style={[
                    styles.brandTitle,
                    { color: theme.primary, fontFamily: Fonts?.serif },
                  ]}>
                  RIVIERA
                </Text>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t('a11y.closeMenu')}
                onPress={() => setOpen(false)}
                style={({ pressed }) => [styles.iconSquare, pressed && styles.pressed]}>
                <X size={20} color={theme.text} />
              </Pressable>
            </View>
            <View style={{ paddingVertical: Spacing.two }}>
              {navLinks.map((link) => (
                <Pressable
                  key={link.key}
                  onPress={() => {
                    setOpen(false);
                    goTo(link.pathname);
                  }}
                  style={({ pressed }) => [
                    styles.navLink,
                    pressed && { backgroundColor: theme.secondary },
                  ]}>
                  <Text style={{ color: theme.text, fontSize: 15, fontWeight: '500' }}>
                    {t(`nav.${link.key}`)}
                  </Text>
                </Pressable>
              ))}
            </View>
          </SafeAreaView>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  brandStack: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: Spacing.half,
  },
  brandTitle: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 1.2,
    textAlign: 'center',
  },
  brandSubRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: 6,
    marginTop: 2,
  },
  brandSubRule: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 4,
    height: 1,
  },
  brandSub: {
    fontSize: 9,
    fontWeight: '600',
    letterSpacing: 3,
    flexShrink: 0,
  },
  desktopNavLink: {
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  iconBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: Spacing.one,
    paddingVertical: 6,
    borderRadius: Radius.md,
  },
  iconSquare: {
    width: 36,
    height: 36,
    borderRadius: Radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: {
    opacity: 0.6,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  modalSheet: {
    paddingHorizontal: Spacing.three,
    paddingBottom: Spacing.three,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: Spacing.two,
  },
  navLink: {
    paddingVertical: Spacing.two + 4,
    paddingHorizontal: Spacing.two,
    borderRadius: Radius.md,
  },
});
